import { Preferences } from '@citruscamps/citrus-client'
import React from 'react'
import { ClassNamesConfig } from 'react-select'

const defaultBrandAccentColor = '#2a7953'
const defaultDark = '#343A40'
export const DefaultLight = '#F8F9FA'

export const addAlpha = (color: string, opacity: number): string => {
  // coerce values so ti is between 0 and 1.
  const _opacity = Math.round(Math.min(Math.max(opacity || 1, 0), 1) * 255)
  return color + _opacity.toString(16).toUpperCase()
}

export const calculateRGB = (color: string): { R: number; G: number; B: number } => {
  color = color.includes('#') ? color : `#${color}`
  let R = parseInt(color.substring(1, 3), 16)
  let G = parseInt(color.substring(3, 5), 16)
  let B = parseInt(color.substring(5, 7), 16)
  R = parseInt(`${(R * 100) / 100}`)
  G = parseInt(`${(G * 100) / 100}`)
  B = parseInt(`${(B * 100) / 100}`)
  return { R, G, B }
}

export const lighten = (color: string, percent: number): string => {
  let { R, G, B } = calculateRGB(color)

  R = parseInt(`${(R * (100 + percent)) / 100}`)
  G = parseInt(`${(G * (100 + percent)) / 100}`)
  B = parseInt(`${(B * (100 + percent)) / 100}`)

  R = R < 255 ? R : 255
  G = G < 255 ? G : 255
  B = B < 255 ? B : 255

  var RR = R.toString(16).length === 1 ? '0' + R.toString(16) : R.toString(16)
  var GG = G.toString(16).length === 1 ? '0' + G.toString(16) : G.toString(16)
  var BB = B.toString(16).length === 1 ? '0' + B.toString(16) : B.toString(16)

  return '#' + RR + GG + BB
}

export const darken = (color: string, percent: number): string => {
  percent *= -1
  return lighten(color, percent)
}

// https://codepen.io/andreaswik/pen/YjJqpK
export const lightOrDark = (color: string): 'light' | 'dark' => {
  let r: number = 0
  let g: number = 0
  let b: number = 0
  // Check the format of the color, HEX or RGB?
  if (color.match(/^rgb/)) {
    // If HEX --> store the red, green, blue values in separate variables
    const colorSplit: RegExpMatchArray | null = color.match(
      /^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+(?:\.\d+)?))?\)$/,
    )
    if (colorSplit) {
      r = +colorSplit[1]
      g = +colorSplit[2]
      b = +colorSplit[3]
    }
  } else {
    // If RGB --> Convert it to HEX: http://gist.github.com/983661
    const colorNum: number = +('0x' + color.slice(1).replace(color.length > 4 ? '' : /./g, '$&$&'))

    r = colorNum >> 16
    g = (colorNum >> 8) & 255
    b = colorNum & 255
  }

  // HSP equation from http://alienryderflex.com/hsp.html
  const hsp = Math.sqrt(0.299 * (r * r) + 0.587 * (g * g) + 0.114 * (b * b))

  const isBrandColor = color === defaultBrandAccentColor

  // Using the HSP value, determine whether the color is light or dark
  if (hsp <= 127.5 || isBrandColor) {
    return 'dark'
  } else {
    return 'light'
  }
}

export const getSelectClasses = (hasError?: boolean): ClassNamesConfig<any> => {
  return {
    control: () => 'form-control',
    input: () => (hasError ? 'is-invalid' : ''),
    menu: () => 'mt-2 form-control px-0',
    multiValue: () => 'badge text-bg-secondary me-2 align-items-center',
    option: (state) => `px-2 py-1 ${state.isFocused ? 'bg-body-secondary' : ''}`,
  }
}

export const getFieldSelectClasses = (): ClassNamesConfig<any> => {
  return {
    container: () => 'flex-fill',
    control: () => 'form-control',
    menu: () => 'mt-2 form-control p-0',
    menuList: () => 'py-2 menu-list',
    multiValue: () => 'badge text-bg-secondary me-2',
    option: (state) => `px-2 py-1 ${state.isFocused ? 'bg-body-secondary' : ''}`,
  }
}

export const getPrimaryButtonStyles = (
  preferences?: Preferences,
  state?: any,
): Pick<React.CSSProperties, 'backgroundColor' | 'color' | 'borderColor'> => {
  const brand_accent_color: string = getSafeBrandColor(preferences)
  const color = lightOrDark(brand_accent_color) === 'dark' ? DefaultLight : defaultDark
  return {
    backgroundColor: state?.isHovered ? darken(brand_accent_color, 15) : brand_accent_color,
    color,
    borderColor: state?.isHovered ? darken(brand_accent_color, 15) : brand_accent_color,
  }
}

export const getSafeBrandColor = (preferences?: Preferences): string => {
  const brand_accent_color: string = preferences?.brand_accent_color
    ? `#${preferences?.brand_accent_color}`
    : defaultBrandAccentColor
  return lightOrDark(brand_accent_color) === 'light'
    ? darken(brand_accent_color, 50)
    : brand_accent_color
}
